import { useState, useEffect } from 'react';
import { ordersService } from '@/shared/services/ordersService';
import { cashService } from '@/shared/services/cashService';
import { couriersService } from '@/shared/services/couriersService';
import { ConfirmModal } from '@/shared/components/ConfirmModal';
import { SuccessModal } from '@/shared/components/SuccessModal';
import { AlertModal } from '@/shared/components/AlertModal';
import { Order } from '@/shared/types/orders';
import { Courier } from '@/shared/types/couriers';
import styles from './CashHandoversPage.module.css';

// Neto que el repartidor debe entregar (total del pedido - su ganancia)
const getNetToHandOver = (order: Order): number => {
    const recordedExpected = order.payment?.cash?.expectedToHandOverAmount;
    const courierFee = order.financial?.courierDeliveryFee || 0;
    const paymentAmount = order.payment?.amount ?? 0;

    // Smart Logic for transition: pedidos antiguos guardaban el bruto
    const isOldGrossValue = recordedExpected === paymentAmount && courierFee > 0;
    if (isOldGrossValue) return paymentAmount - courierFee;
    return recordedExpected !== undefined ? recordedExpected : paymentAmount - courierFee;
};

export const CashHandoversPage = () => {
    const [orders, setOrders] = useState<Order[]>([]);
    const [couriers, setCouriers] = useState<Record<string, Courier>>({});
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [confirmingBatch, setConfirmingBatch] = useState(false);
    const [modalConfig, setModalConfig] = useState<{
        isOpen: boolean;
        title: string;
        message: string;
        infoHint?: string;
        incidentNote?: string;
        showInput?: boolean;
        inputLabel?: string;
        inputType?: 'text' | 'number' | 'textarea';
        inputRequired?: boolean;
        onConfirm: (val?: string) => void;
    }>({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: () => { }
    });
    const [successModal, setSuccessModal] = useState<{
        isOpen: boolean;
        title: string;
        message: string;
    }>({
        isOpen: false,
        title: '',
        message: ''
    });
    const [alertModal, setAlertModal] = useState<{
        isOpen: boolean;
        title: string;
        message: string;
        type: 'success' | 'error' | 'warning' | 'info';
    }>({
        isOpen: false,
        title: '',
        message: '',
        type: 'info'
    });

    useEffect(() => {
        setLoading(true);
        const unsubscribe = ordersService.subscribe(
            { handoverStatus: 'PENDING' },
            (pendingOrders) => {
                setOrders(pendingOrders);
                setLoading(false);

                const courierIds = Array.from(new Set(
                    pendingOrders
                        .map(o => o.assignedCourierId)
                        .filter(id => !!id)
                )) as string[];

                if (courierIds.length > 0) {
                    fetchCouriers();
                }
            },
            (err) => {
                console.error('Error suscribiendo a entregas:', err);
                setLoading(false);
            }
        );
        return () => unsubscribe();
    }, []);

    const fetchCouriers = async () => {
        try {
            const couriersRes = await couriersService.listAll({ status: 'activo' });
            const courierMap = couriersRes.couriers.reduce((acc, c) => {
                acc[c.uid] = c;
                return acc;
            }, {} as Record<string, Courier>);
            setCouriers(prev => ({ ...prev, ...courierMap }));
        } catch (error) {
            console.error('Error fetching couriers:', error);
        }
    };

    const handleConfirmHandover = (orderId: string) => {
        const order = orders.find(o => o.orderId === orderId);
        let incidentNote = undefined;

        if (order?.incident) {
            incidentNote = `INCIDENCIA: ${order.incident.reasonText}`;
        }

        setModalConfig({
            isOpen: true,
            title: 'Confirmar Entrega Individual',
            message: '¿Confirmas que has recibido el efectivo para este pedido?',
            incidentNote,
            showInput: true,
            inputLabel: 'Monto Recibido',
            inputType: 'number',
            inputRequired: true,
            onConfirm: async (inputValue?: string) => {
                const amountReceived = inputValue ? parseFloat(inputValue) : undefined;
                console.log('DEBUG HANDOVER:', { orderId, inputValue, amountReceived });

                setConfirmingBatch(true);
                try {
                    await cashService.confirmHandover(orderId, amountReceived);
                    setSuccessModal({
                        isOpen: true,
                        title: '¡Confirmado!',
                        message: 'La entrega de efectivo individual ha sido registrada correctamente.'
                    });
                } catch (error) {
                    console.error('Error confirming handover:', error);
                    setAlertModal({
                        isOpen: true,
                        title: 'Error',
                        message: 'No se pudo confirmar la entrega de efectivo individual.',
                        type: 'error'
                    });
                } finally {
                    setConfirmingBatch(false);
                    setModalConfig(prev => ({ ...prev, isOpen: false }));
                }
            }
        });
    };

    const handleBatchConfirm = (courierId: string, orderIds: string[], totalAmount: number) => {
        const courier = couriers[courierId];
        let infoHint = undefined;
        let incidentNote = undefined;

        // Si el repartidor tiene saldo a favor (tarjetas) que descuentan el efectivo
        const cashBalance = courier?.wallet?.cashBalance;
        if (typeof cashBalance === 'number' && cashBalance < totalAmount) {
            infoHint = `Monto sugerido con cruce de tarjetas: $${cashBalance.toLocaleString()}`;
        }

        // Recopilar incidencias del lote
        const incidentOrders = orders.filter(o => orderIds.includes(o.orderId) && o.incident);
        if (incidentOrders.length > 0) {
            const firstIncident = incidentOrders[0]?.incident;
            if (incidentOrders.length === 1 && firstIncident) {
                incidentNote = `ATENCIÓN: 1 pedido tiene incidencia: ${firstIncident.reasonText}`;
            } else {
                incidentNote = `ATENCIÓN: ${incidentOrders.length} pedidos tienen incidencias. Por favor verifica las notas individuales.`;
            }
        }

        setModalConfig({
            isOpen: true,
            title: 'Confirmar Arqueo Masivo',
            message: `¿Confirmas que has recibido el dinero de los ${orderIds.length} pedidos de este repartidor?`,
            infoHint,
            incidentNote,
            showInput: true,
            inputLabel: 'Monto Recibido',
            inputType: 'number',
            inputRequired: true,
            onConfirm: async (inputValue?: string) => {
                const amountReceived = inputValue ? parseFloat(inputValue) : totalAmount;
                if (isNaN(amountReceived)) {
                    setAlertModal({
                        isOpen: true,
                        title: 'Monto Inválido',
                        message: 'Por favor, ingresa un monto numérico válido.',
                        type: 'warning'
                    });
                    return;
                }

                setConfirmingBatch(true);
                try {
                    const response = await cashService.batchConfirmHandover({
                        courierId,
                        orderIds,
                        amountReceived
                    });

                    if (response.success) {
                        setSuccessModal({
                            isOpen: true,
                            title: '¡Arqueo Exitoso!',
                            message: `Se han procesado correctamente los ${orderIds.length} pedidos por un total de $${totalAmount.toLocaleString()}.`
                        });
                    } else {
                        setAlertModal({
                            isOpen: true,
                            title: 'Atención',
                            message: response.message,
                            type: 'warning'
                        });
                    }
                } catch (error: any) {
                    console.error('Error in batch confirmation:', error);
                    const errorMsg = error.response?.data?.message || 'Error al procesar el arqueo masivo.';
                    setAlertModal({
                        isOpen: true,
                        title: 'Error de Arqueo',
                        message: errorMsg,
                        type: 'error'
                    });
                } finally {
                    setConfirmingBatch(false);
                    setModalConfig(prev => ({ ...prev, isOpen: false }));
                }
            }
        });
    };

    const formatDateTime = (timestamp?: { seconds: number } | null) => {
        if (!timestamp?.seconds) return '—';
        return new Date(timestamp.seconds * 1000).toLocaleString();
    };

    // Agrupar pedidos por Repartidor y filtrar por búsqueda
    const groupedOrders = orders.reduce((acc, order) => {
        // Filtrar pedidos que están en reporte de incidencia (se gestionan en su propio módulo)
        if (order.status === 'Incidencia') return acc;

        const courierId = order.assignedCourierId || 'Sin Repartidor';
        const courier = couriers[courierId];

        // Lógica de filtrado
        if (searchTerm.trim() !== '') {
            const lowerSearch = searchTerm.toLowerCase();
            const matchesId = courierId.toLowerCase().includes(lowerSearch);
            const matchesName = courier?.fullName?.toLowerCase().includes(lowerSearch);
            const matchesEmail = courier?.email?.toLowerCase().includes(lowerSearch);

            if (!matchesId && !matchesName && !matchesEmail) {
                return acc;
            }
        }

        if (!acc[courierId]) {
            acc[courierId] = {
                orders: [],
                total: 0
            };
        }

        const amountToHandOver = getNetToHandOver(order);

        acc[courierId].orders.push(order);
        acc[courierId].total += amountToHandOver;

        return acc;
    }, {} as Record<string, { orders: Order[], total: number }>);

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div>
                    <h1>Entregas de Efectivo</h1>
                    <p className={styles.subtitle}>Gestión de arqueo por repartidor</p>
                </div>

                <div className={styles.searchContainer}>
                    <input
                        type="text"
                        placeholder="Buscar por nombre, email o ID..."
                        className={styles.searchInput}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                        <button className={styles.clearSearch} onClick={() => setSearchTerm('')}>
                            ✕
                        </button>
                    )}
                </div>
            </div>

            <div className={styles.tableContainer}>
                {loading ? (
                    <div className={styles.emptyState}>Cargando pedidos pendientes...</div>
                ) : Object.keys(groupedOrders).length === 0 ? (
                    <div className={styles.emptyState}>
                        {searchTerm ? 'No se encontraron repartidores que coincidan con la búsqueda.' : 'No hay entregas de efectivo pendientes.'}
                    </div>
                ) : (
                    Object.entries(groupedOrders).map(([courierId, group]) => {
                        const courier = couriers[courierId];
                        const displayName = courier ? `${courier.fullName}` : `ID: ${courierId}`;
                        const displayEmail = courier?.email || 'Correo no disponible';
                        // El backend puede crear wallet solo con cashBalance (increment) sin creditLimit
                        const walletBalance = courier?.wallet?.cashBalance ?? 0;
                        const walletLimit = courier?.wallet?.creditLimit;
                        const isOverLimit = typeof walletLimit === 'number' && walletBalance >= walletLimit;

                        return (
                            <div key={courierId} className={styles.courierGroup}>
                                <div className={styles.groupHeader}>
                                    <div className={styles.courierInfo}>
                                        <span className={styles.courierIdLabel}>Repartidor:</span>
                                        <span className={styles.courierName}>{displayName}</span>
                                        <span className={styles.courierEmail}>{displayEmail}</span>
                                        {courier?.wallet && (
                                            <div className={styles.walletBadge} style={{
                                                marginLeft: '12px',
                                                padding: '2px 8px',
                                                borderRadius: '4px',
                                                fontSize: '0.8rem',
                                                backgroundColor: isOverLimit ? '#fee2e2' : '#f3f4f6',
                                                color: isOverLimit ? '#dc2626' : '#374151',
                                                border: `1px solid ${isOverLimit ? '#fecaca' : '#e5e7eb'}`,
                                                fontWeight: 600
                                            }}>
                                                Deuda Real (Neto): ${walletBalance.toLocaleString()} / {typeof walletLimit === 'number' ? `$${walletLimit.toLocaleString()}` : 'Sin límite asignado'}
                                            </div>
                                        )}
                                    </div>
                                    <div className={styles.groupActions}>
                                        <div className={styles.groupTotal}>
                                            <span>Total Pendiente:</span>
                                            <strong>${group.total.toFixed(2)}</strong>
                                        </div>
                                        <button
                                            className={styles.batchButton}
                                            onClick={() => handleBatchConfirm(courierId, group.orders.map(o => o.orderId), group.total)}
                                            disabled={confirmingBatch}
                                        >
                                            Confirmar Todo de este Repartidor
                                        </button>
                                    </div>
                                </div>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Pedido ID</th>
                                            <th>Fecha/Hora</th>
                                            <th>Total</th>
                                            <th>Ganancia Rep.</th>
                                            <th>A Recibir (Neto)</th>
                                            <th>Origen (Restaurante)</th>
                                            <th>Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {group.orders.map((order) => {
                                            const recordedExpected = order.payment?.cash?.expectedToHandOverAmount;
                                            const courierFee = order.financial?.courierDeliveryFee || 0;
                                            const paymentAmount = order.payment?.amount ?? 0;
                                            const netToDisplay = getNetToHandOver(order);

                                            const hasDiscrepancy = (recordedExpected !== undefined && Math.abs(netToDisplay - (paymentAmount - courierFee)) > 0.01) || !!order.incident;

                                            return (
                                                <tr key={order.orderId} className={hasDiscrepancy ? styles.discrepancyRow : ''}>
                                                    <td>#{order.orderCode}</td>
                                                    <td>{formatDateTime(order.createdAt)}</td>
                                                    <td className={styles.amount}>
                                                        ${paymentAmount.toLocaleString()}
                                                    </td>
                                                    <td className={styles.amount} style={{ color: '#6b7280' }}>
                                                        -${(order.financial?.courierDeliveryFee || 0).toLocaleString()}
                                                    </td>
                                                    <td className={`${styles.amount} ${hasDiscrepancy ? styles.discrepancyAmount : ''}`} style={{ fontWeight: 600 }}>
                                                        ${netToDisplay.toLocaleString()}
                                                        {hasDiscrepancy && (
                                                            <span className={styles.discrepancyTag} title="Monto ajustado por el repartidor">
                                                                (Ajustado)
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td>{order.pickup?.restaurantName || '—'}</td>
                                                    <td className={styles.actions}>
                                                        <button
                                                            className={styles.confirmButton}
                                                            onClick={() => handleConfirmHandover(order.orderId)}
                                                            disabled={confirmingBatch}
                                                        >
                                                            Individual
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        );
                    })
                )}
            </div>

            <ConfirmModal
                isOpen={modalConfig.isOpen}
                onClose={() => setModalConfig(prev => ({ ...prev, isOpen: false }))}
                onConfirm={modalConfig.onConfirm}
                title={modalConfig.title}
                message={modalConfig.message}
                infoHint={modalConfig.infoHint}
                incidentNote={modalConfig.incidentNote}
                showInput={modalConfig.showInput}
                inputLabel={modalConfig.inputLabel}
                inputType={modalConfig.inputType}
                inputRequired={modalConfig.inputRequired}
                isLoading={confirmingBatch}
                confirmText="Sí, Confirmar"
                cancelText="No, Volver"
            />

            <SuccessModal
                isOpen={successModal.isOpen}
                onClose={() => setSuccessModal(prev => ({ ...prev, isOpen: false }))}
                title={successModal.title}
                message={successModal.message}
            />

            <AlertModal
                isOpen={alertModal.isOpen}
                onClose={() => setAlertModal(prev => ({ ...prev, isOpen: false }))}
                title={alertModal.title}
                message={alertModal.message}
                type={alertModal.type}
            />
        </div>
    );
};
